import { setLocalStorageItem } from '@snapchat/mw-common';
import type { FormBody } from '@snapchat/snap-design-system-marketing';
import pick from 'lodash/pick.js';
import { type FC, type ReactNode, useEffect, useState } from 'react';

import type { AnalyticsDataProps } from '../../../../components/Analytics/types';
import type {
  ContentfulExtraParametersFormFeature,
  ContentfulFormRow,
} from '../../../../components/Form';
import { Form } from '../../../../components/Form/Form';
import { Redirect } from '../../../../components/Redirect/Redirect';
import type { Items } from '../../../../types/Items';
import type { RichText } from '../../../../types/RichText';
import { renderRichTextMarkingsOnly } from '../../../../utils/renderText/renderRichText';
import { RegistrationDestination } from '../../types';
import { getRegistrationLocalStorageData } from '../../utils/getRegistrationLocalStorageData';
import type { AnyConnectorType } from '../types';

interface RegistrationFormProps {
  id: string;
  formAnalytics: AnalyticsDataProps;
  formRowsCollection: Items<ContentfulFormRow>;
  additionalFeaturesCollection?: Items<ContentfulExtraParametersFormFeature>;
  rememberRegistration: boolean;
  confirmationUrl: string;
  unknownErrorText: RichText;
  connector: AnyConnectorType;
  storedFields?: string[];
}

export const RegistrationForm: FC<RegistrationFormProps> = ({
  id,
  formAnalytics,
  formRowsCollection,
  additionalFeaturesCollection,
  rememberRegistration,
  confirmationUrl,
  unknownErrorText,
  connector,
  storedFields = [],
}) => {
  // State
  const [successfullyRegistered, setSuccessfullyRegistered] = useState<boolean>(false);

  // Callback for form submission
  const onFormSubmit = (formBody: FormBody, response?: Response) => {
    if (response?.ok) {
      // Store any fields that were provided and match what is given in "storedFields"
      // TODO: Test once UI is connected to backend via "CVent/Salesforce Connector"
      const storedFieldsData = pick(formBody, storedFields);

      const localRegistrationData = getRegistrationLocalStorageData();
      const newLocalStorageData = {
        ...localRegistrationData,
        [id]: storedFieldsData,
      };
      setLocalStorageItem('experience-registration', JSON.stringify(newLocalStorageData));
      setSuccessfullyRegistered(true);
      return;
    }
  };

  const renderErrorMessage = (statusCode: number): ReactNode => {
    if (
      statusCode === 401 &&
      (connector.__typename === 'CventConnector' || connector.__typename === 'ZkipsterConnector')
    ) {
      return renderRichTextMarkingsOnly(connector.allowlistErrorText);
    }

    return renderRichTextMarkingsOnly(unknownErrorText);
  };

  // Check if the user has already registered
  useEffect(() => {
    const localRegistrationData = getRegistrationLocalStorageData();
    const hasRegistered = localRegistrationData[id] !== undefined;
    setSuccessfullyRegistered(rememberRegistration && hasRegistered);
  }, [id, rememberRegistration]);

  // If the user has already registered, redirect them to given confirmation URL
  if (successfullyRegistered) {
    return <Redirect path={`/${confirmationUrl}`} />;
  }

  // Tack on params from the connector so the correct handler is used
  let extraParams: Record<string, string> = {};

  // TODO: It might be cleaner to simply pass in the connector id and then the backend can query contentful
  // to get whatever fields on the connector that is needed
  // JIRA: https://jira.sc-corp.net/browse/WEBP-12607

  if (connector.__typename === 'SalesforceConnector') {
    extraParams = {
      destination: RegistrationDestination.Salesforce,
      Form_Category__c: connector.category ?? '',
      Year__c: connector.year ?? '',
      emailTemplateId: connector.confirmationEmail?.sys.id ?? '',
    };
  } else if (connector.__typename === 'SplashConnector') {
    extraParams = {
      destination: RegistrationDestination.Splash,
      eventId: connector.eventId ?? '',
      emailTemplateId: connector.confirmationEmail?.sys.id ?? '',
    };
  } else if (connector.__typename === 'CventConnector') {
    extraParams = {
      destination: RegistrationDestination.Cvent,
      eventId: connector.eventId ?? '',
      admissionItemId: connector.admissionItemId ?? '',
      invitationListId: connector.invitationListId ?? '',
    };
  } else if (connector.__typename === 'ZkipsterConnector') {
    extraParams = {
      destination: RegistrationDestination.Zkipster,
      eventId: connector.eventId ?? '',
      guestListId: connector.guestListId ?? '',
      connectorId: connector.sys.id ?? '',
      emailTemplateId: connector.confirmationEmail?.sys.id ?? '',
    };
  } else {
    throw new Error('Supported connector not found.');
  }

  return (
    <div>
      <Form
        endpoint="/api/register"
        submitText="Submit"
        submitSuccessText="Success"
        analytics={formAnalytics}
        rowsCollection={formRowsCollection}
        additionalFeaturesCollection={additionalFeaturesCollection}
        sys={{ id: `form-${id}` }}
        callback={onFormSubmit}
        extraParams={extraParams}
        renderErrorMessage={renderErrorMessage}
      />
    </div>
  );
};
